
import React, { useState, useMemo } from 'react';
import RecipeAiProgressOverlay from './RecipeAiProgressOverlay';
import { useAiWorkflowProgress } from '../hooks/useAiWorkflowProgress';
import { AI_PARSE_LAST_STEP_INDEX } from '../utils/aiWorkflowSteps';
import { isRecipeUrl, parseRecipeWithGemini, RecipeParseError, getRecipeParseErrorMessage, type InstagramOwner } from '../services/geminiService';
import type { MealType, Recipe, User, Language, Chef } from '../types';
import XIcon from './icons/XIcon';
import SparklesIcon from './icons/SparklesIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import CheckSquareIcon from './icons/CheckSquareIcon';
import { getTranslation, isRTL } from '../utils/translations';
import { formatIngredientQuantity } from '../utils/unitConversion';
import { creditChefFromParsedRecipe } from '../utils/resolveRecipeChefCredit';
import { findConflictingDiscoverableRecipeForUrl } from '../utils/recipeExternalVideoDuplicate';
import { sanitizeExternalHrefs } from '../utils/safeUrl';
import InfoNoticeModal from './InfoNoticeModal';
import RecipeContentRejectionModal from './RecipeContentRejectionModal';
import { screenRecipeTextLocally } from '../utils/recipeContentGuard';
import { reportAndShowContentRejection, type ContentRejectionModalState } from '../utils/recipeContentRejection';

interface ChefAssistantModalProps {
    onClose: () => void;
    onAddRecipe: (recipe: Omit<Recipe, 'id'>) => Promise<string | void> | void;
    currentUser: User | null;
    chefs?: Chef[];
    allRecipes?: Recipe[];
    onResolveInstagramChef?: (owner: InstagramOwner) => Promise<Chef | null>;
    onViewExistingRecipe?: (recipe: Recipe) => void;
}

const ChefAssistantModal: React.FC<ChefAssistantModalProps> = ({ onClose, onAddRecipe, currentUser, chefs = [], allRecipes = [], onResolveInstagramChef, onViewExistingRecipe }) => {
    const [text, setText] = useState('');
    const [status, setStatus] = useState<'input' | 'processing' | 'review'>('input');
    const [parsedRecipe, setParsedRecipe] = useState<Omit<Recipe, 'id'> | null>(null);
    const [error, setError] = useState('');
    const [contentRejection, setContentRejection] = useState<ContentRejectionModalState | null>(null);
    const [sessionChefs, setSessionChefs] = useState<Chef[]>([]);
    const [existingRecipeConflict, setExistingRecipeConflict] = useState<Recipe | null>(null);
    const {
        advanceTo: advanceAiStep,
        reset: resetAiProgress,
        startAutoAdvance,
        stopAutoAdvance,
        stepKey: aiStepKey,
        stepIndex: aiStepIndex,
    } = useAiWorkflowProgress();

    const availableChefs = useMemo(() => {
        const byId = new Map(chefs.map((chef) => [chef.id, chef]));
        sessionChefs.forEach((chef) => byId.set(chef.id, chef));
        return Array.from(byId.values());
    }, [chefs, sessionChefs]);

    const lang = currentUser?.language || 'en';
    const t = (key: any) => getTranslation(lang, key);
    const rtl = isRTL(lang);

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setText(text);
        } catch (err) {
            console.error('Failed to read clipboard', err);
        }
    };

    const handleAnalyze = async () => {
        if (!text.trim()) return;
        resetAiProgress();
        advanceAiStep(0);
        setStatus('processing');
        startAutoAdvance(AI_PARSE_LAST_STEP_INDEX - 1);
        setError('');

        try {
            const textToAnalyze = text.trim();
            const isUrl = isRecipeUrl(textToAnalyze);
            const initialLink = isUrl ? textToAnalyze : undefined;

            if (initialLink) {
                const conflict = await findConflictingDiscoverableRecipeForUrl(initialLink, {
                    knownRecipes: allRecipes,
                });
                if (conflict) {
                    setExistingRecipeConflict(conflict.recipe);
                    setStatus('input');
                    return;
                }
            }

            const parseOptions = initialLink
                ? { url: initialLink, userId: currentUser?.uid }
                : { userId: currentUser?.uid };

            const parsed = await parseRecipeWithGemini(
                isUrl ? '' : textToAnalyze,
                lang,
                parseOptions,
                !currentUser?.isAiEligible ? currentUser?.encryptedGeminiKey : undefined
            );

            stopAutoAdvance();
            advanceAiStep(AI_PARSE_LAST_STEP_INDEX);
            
            let finalLinks = sanitizeExternalHrefs(parsed.links || []);
            if (initialLink) {
                finalLinks = sanitizeExternalHrefs([initialLink, ...finalLinks]);
            }
            
            const recipeData: Omit<Recipe, 'id'> = {
                ...parsed,
                links: finalLinks,
                externalVideoId: parsed.externalVideoId,
                externalVideoIdAliases: parsed.externalVideoIdAliases,
                ownerId: currentUser?.uid || '',
                householdId: currentUser?.activeHouseholdId || '',
                isDeleted: false,
            };

            let resolvedChefId: string | null = null;
            const credited = await creditChefFromParsedRecipe(parsed, availableChefs, {
                userId: currentUser?.uid,
                onResolveInstagramChef,
                extraUrls: finalLinks,
            });
            if (credited.chef) {
                setSessionChefs((prev) => {
                    if (prev.some((chef) => chef.id === credited.chef!.id)) return prev;
                    return [...prev, credited.chef!];
                });
                resolvedChefId = credited.chef.id;
            }
            if (resolvedChefId) {
                recipeData.chefId = resolvedChefId;
            }
            
            setParsedRecipe(recipeData);
            setStatus('review');
        } catch (e) {
            console.error(e);
            if (e instanceof RecipeParseError && e.code === 'instagram_unavailable') {
                setError(t('scraperUnavailableMsg'));
            } else if (
                e instanceof RecipeParseError
                && (e.code === 'not_a_recipe' || e.code === 'inappropriate_content')
            ) {
                setContentRejection({
                    isOpen: true,
                    variant: e.code === 'inappropriate_content' ? 'misconduct' : 'not_recipe',
                });
            } else {
                setError(getRecipeParseErrorMessage(e, t));
            }
            setStatus('input');
        } finally {
            stopAutoAdvance();
        }
    };

    const handleSave = async () => {
        if (parsedRecipe) {
            const instructionSteps = Array.isArray(parsedRecipe.instructions)
                ? parsedRecipe.instructions
                : [{ id: 'legacy', type: 'step' as const, content: String(parsedRecipe.instructions || '') }];

            // Save-time moderation: block saving non-recipes or inappropriate text.
            const candidateText = [
                parsedRecipe.name,
                (parsedRecipe as { originNote?: string }).originNote ?? '',
                parsedRecipe.ingredients.map((i) => i.name).join(' '),
                instructionSteps.map((s) => s.content).join(' '),
            ].join('\n');

            const localScreen = screenRecipeTextLocally(candidateText);
            if (!localScreen.allowed) {
                await reportAndShowContentRejection(
                    {
                        userId: currentUser?.uid,
                        code: localScreen.code ?? 'not_a_recipe',
                        source: 'recipe_save',
                        textSample: candidateText,
                    },
                    setContentRejection,
                );
                return;
            }

            const hasIngredientText = parsedRecipe.ingredients.some((i) => i.name.trim().length > 0);
            const hasInstructionText = instructionSteps.some(
                (s) => (s.type === 'step' || s.type === 'header') && s.content.trim().length > 0,
            );
            if (!hasIngredientText || !hasInstructionText) {
                setContentRejection({ isOpen: true, variant: 'not_recipe' });
                return;
            }

            const canUseAi = !!(currentUser?.isAiEligible || currentUser?.encryptedGeminiKey);
            const originLanguage = (parsedRecipe.originLanguage || lang) as Language;
            const needsBackgroundFinalize = canUseAi;
            const recipeToSave: Omit<Recipe, 'id'> = {
                ...parsedRecipe,
                instructions: instructionSteps,
                originLanguage,
                saveStatus: needsBackgroundFinalize ? 'pending' : 'ready',
            };

            await onAddRecipe(recipeToSave);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-bg-cream dark:bg-slate-950 z-[200] flex flex-col font-rubik transition-colors duration-200">
            {status === 'processing' && (
                <RecipeAiProgressOverlay
                    stepKey={aiStepKey}
                    stepIndex={aiStepIndex}
                    language={lang}
                />
            )}
            {/* Header */}
            <div className="h-16 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md flex items-center justify-between px-6 border-b border-stone-200 dark:border-slate-800 shrink-0">
                <div className="flex items-center gap-3">
                    {status !== 'input' && (
                        <button onClick={() => setStatus('input')} className="p-2 -ml-2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
                            <ArrowLeftIcon className="w-6 h-6 rtl:rotate-180" />
                        </button>
                    )}
                    <div className="p-2 bg-gradient-to-br from-brand-orange to-amber-500 rounded-xl text-white shadow-sm">
                        <SparklesIcon className="w-5 h-5" />
                    </div>
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                        {status === 'review' ? t('reviewingNewMeal') : t('whatAreWeCooking')}
                    </h2>
                </div>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-200 transition-all">
                    <XIcon className="w-8 h-8" />
                </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 scrollbar-none">
                <div className="max-w-xl mx-auto h-full flex flex-col">
                    
                    {status === 'input' && (
                        <div className="flex flex-col gap-6 animate-fade-in h-full">
                            {!currentUser?.isAiEligible && !currentUser?.encryptedGeminiKey && (
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 p-4 rounded-2xl flex items-start gap-3">
                                    <SparklesIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-tight">{t('aiNotEligible')}</p>
                                        <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium leading-tight mt-1">Upgrade your account or contact support to enable AI features.</p>
                                    </div>
                                </div>
                            )}

                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] shadow-sm border border-stone-100 dark:border-slate-800 flex-grow flex flex-col">
                                <label className="block text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">
                                    {t('chefAssistantSubtitle')}
                                </label>
                                <textarea 
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t('chefAssistantPlaceholder')}
                                    className="w-full flex-grow bg-slate-50 dark:bg-slate-800 border-none rounded-2xl p-4 text-base text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:ring-0 resize-none leading-relaxed"
                                />
                                <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <button 
                                        onClick={handlePaste}
                                        className="flex items-center gap-2 text-xs font-bold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors px-2 py-1 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20"
                                    >
                                        <ClipboardIcon className="w-4 h-4" />
                                        {t('pasteFromClipboard')}
                                    </button>
                                    <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase">{text.length} chars</span>
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-2xl text-red-600 dark:text-red-400 text-sm font-bold text-center">
                                    {error}
                                </div>
                            )}

                            <div className="mt-auto pt-4">
                                <button 
                                    onClick={handleAnalyze}
                                    disabled={!text.trim() || (!currentUser?.isAiEligible && !currentUser?.encryptedGeminiKey)}
                                    className="w-full py-4 bg-brand-orange text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-brand-orange/20 hover:bg-orange-600 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                                >
                                    <SparklesIcon className="w-5 h-5" />
                                    {t('analyzeWithAI')}
                                </button>
                            </div>
                        </div>
                    )}

                    {status === 'review' && parsedRecipe && (
                        <div className="flex flex-col gap-6 animate-fade-in pb-24">
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-stone-100 dark:border-slate-800 shadow-sm">
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">{t('recipeName')}</label>
                                <input 
                                    type="text" 
                                    value={parsedRecipe.name}
                                    onChange={(e) => setParsedRecipe({ ...parsedRecipe, name: e.target.value })}
                                    className="w-full text-2xl font-black text-brand-sage dark:text-brand-sage bg-transparent border-none focus:ring-0 p-0 placeholder:text-slate-300 dark:placeholder:text-slate-600"
                                />
                                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <span className="text-[10px] font-black uppercase tracking-widest bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-3 py-1.5 rounded-full">
                                        {parsedRecipe.servings} {t('servings')}
                                    </span>
                                    {(parsedRecipe.suitableFor || []).map((meal: MealType) => (
                                        <span key={meal} className="text-[10px] font-black uppercase tracking-widest bg-orange-50 dark:bg-orange-900/20 text-brand-orange px-3 py-1.5 rounded-full">
                                            {t(meal === 'Breakfast' ? 'morning' : meal === 'Lunch' ? 'noon' : 'evening')}
                                        </span>
                                    ))}
                                    {(parsedRecipe.dietTypes?.isKeto ?? parsedRecipe.isKeto) && (
                                        <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-50 dark:bg-emerald-900/20 text-brand-sage px-3 py-1.5 rounded-full">
                                            {t('isKeto')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-stone-100 dark:border-slate-800 shadow-sm">
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <CheckSquareIcon className="w-4 h-4 text-brand-orange" />
                                    {t('ingredientsList')}
                                </h3>
                                <div className="space-y-3">
                                    {parsedRecipe.ingredients.map((ing, idx) => (
                                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-800 last:border-0">
                                            <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">{ing.name}</span>
                                            <span className="font-mono text-xs text-slate-400 dark:text-slate-500 font-bold bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded">
                                                {formatIngredientQuantity(ing.quantity, ing.unit, t)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="fixed bottom-0 left-0 w-full p-4 bg-white dark:bg-slate-900 border-t border-stone-100 dark:border-slate-800 z-50 flex justify-center">
                                <div className="w-full max-w-xl flex gap-3">
                                    <button 
                                        onClick={() => setStatus('input')}
                                        className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-black rounded-2xl text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button 
                                        onClick={handleSave}
                                        className="flex-[2] py-4 bg-brand-sage text-white font-black rounded-2xl shadow-xl shadow-brand-sage/20 hover:opacity-90 active:scale-95 transition-all text-sm uppercase tracking-widest"
                                    >
                                        {t('saveToKitchen')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <InfoNoticeModal
                isOpen={!!existingRecipeConflict}
                onClose={() => setExistingRecipeConflict(null)}
                title={t('recipeAlreadyExistsTitle')}
                message={existingRecipeConflict ? t('recipeAlreadyExistsMsg').replace('{name}', existingRecipeConflict.name) : undefined}
                variant="info"
                language={lang}
                actionLabel={onViewExistingRecipe ? t('viewRecipe') : undefined}
                onAction={existingRecipeConflict && onViewExistingRecipe
                    ? () => onViewExistingRecipe(existingRecipeConflict)
                    : undefined}
            />
            <RecipeContentRejectionModal
                isOpen={!!contentRejection?.isOpen}
                onClose={() => setContentRejection(null)}
                variant={contentRejection?.variant ?? 'not_recipe'}
                language={lang}
            />
        </div>
    );
};

export default ChefAssistantModal;
